import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { convex, convexClient } from '../convexClient'
import { api } from '../lib/convexHelpers'
import toast from 'react-hot-toast'
import useProcessingStore from './processingStore'
import { songsAPI } from '../services/api'

/**
 * Queue store - handles the song queue
 * Manages queue items, adding, removing, and reordering
 *
 * Queue data is synced from Convex reactive queries via useConvexSync.
 * Mutations go directly to Convex with optimistic updates.
 */
const useQueueStore = create((set, get) => ({
  // State
  queue: [],

  // Actions

  /**
   * Set the queue (called by useConvexSync)
   */
  setQueue: (queue) => {
    set({ queue: [...queue] })
  },

  /**
   * Get previous song relative to cursor
   */
  getPreviousSong: (queueOrCursor, maybeCursor) => {
    const queue = Array.isArray(queueOrCursor) ? queueOrCursor : get().queue
    const cursor = Array.isArray(queueOrCursor) ? maybeCursor : queueOrCursor
    const previousItems = queue
      .filter(item => typeof item.position === 'number' && item.position < cursor)
      .sort((a, b) => b.position - a.position)

    return previousItems[0] || null
  },

  /**
   * Get song at current cursor position
   */
  getCurrentSong: (queueOrCursor, maybeCursor) => {
    const queue = Array.isArray(queueOrCursor) ? queueOrCursor : get().queue
    const cursor = Array.isArray(queueOrCursor) ? maybeCursor : queueOrCursor
    return queue.find(item => item.position === cursor) || null
  },

  /**
   * Get next song after cursor
   */
  getNextSong: (queueOrCursor, maybeCursor) => {
    const queue = Array.isArray(queueOrCursor) ? queueOrCursor : get().queue
    const cursor = Array.isArray(queueOrCursor) ? maybeCursor : queueOrCursor
    const nextItems = queue
      .filter(item => typeof item.position === 'number' && item.position > cursor)
      .sort((a, b) => a.position - b.position)

    return nextItems[0] || null
  },

  /**
   * Get active queue items (not played)
   */
  getActiveQueue: () => {
    const { queue } = get()
    return queue.filter(item => item.status !== 'played')
  },

  /**
   * Get history (played items)
   */
  getHistory: () => {
    const { queue } = get()
    return queue.filter(item => item.status === 'played')
  },

  /**
   * Add song to queue via Convex mutation
   */
  addToQueue: async (roomCode, roomId, songId, addedBy = 'anonymous', searchContext = {}) => {
    let effectiveRoomId = roomId

    if (!effectiveRoomId && roomCode) {
      try {
        const room = await convexClient.query(api.rooms.getByCode, { code: roomCode })
        effectiveRoomId = room?._id ?? room?.id ?? null
      } catch (error) {
        console.error('Failed to resolve room ID before queue add:', error)
      }
    }

    if (!effectiveRoomId) {
      toast.error('Room not connected. Please refresh the page.')
      throw new Error('Room not connected.')
    }

    const { queue } = get()
    const { songs = [], searchResults = [] } = searchContext

    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(16).slice(2)}`

    // Find song metadata from various sources
    const findSongMetadata = () => {
      const collections = [queue, searchResults, songs]
      for (const collection of collections) {
        if (!Array.isArray(collection)) continue
        const match = collection.find(item => item?.id === songId || item?.songId === songId)
        if (match) return match
      }
      return null
    }

    const songMetadata = findSongMetadata()

    const existingPositions = queue
      .map(item => typeof item.position === 'number' ? item.position : null)
      .filter(position => position !== null)

    const nextPosition = existingPositions.length
      ? Math.max(...existingPositions) + 1
      : 0

    const hasActiveQueueItems = queue.some(
      item => item.status === 'playing' || item.status === 'queued'
    )

    const optimisticItem = {
      id: optimisticId,
      songId,
      position: nextPosition,
      song: {
        id: songMetadata?.id ?? songMetadata?._id ?? songId,
        youtubeId: songMetadata?.youtubeId ?? null,
        title: songMetadata?.title ?? 'Song added to queue',
        artist: songMetadata?.artist ?? 'Unknown',
        duration: typeof songMetadata?.duration === 'number' ? songMetadata.duration : null,
        thumbnailUrl: songMetadata?.thumbnailUrl ?? null,
      },
      addedBy,
      createdAt: Date.now(),
      status: hasActiveQueueItems ? 'queued' : 'playing',
      optimistic: true
    }

    // Optimistic update
    set(state => {
      const updatedQueue = [...state.queue.filter(item => item.id !== optimisticId), optimisticItem]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      return { queue: updatedQueue }
    })

    try {
      const result = await convex.mutation(api.queue.addToQueue, {
        roomId: effectiveRoomId,
        songId,
        addedBy,
      })

      if (result?.processing) {
        set(state => ({
          queue: state.queue.filter(item => item.id !== optimisticId)
        }))

        if (result.jobId) {
          useProcessingStore.getState().addProcessingJob({
            ...result,
            id: result.jobId,
            jobId: result.jobId,
            songId,
            addedBy,
            status: result.status || 'pending',
            currentStage: result.currentStage || 'cleaning_lyrics',
            autoQueue: true,
            song: result.song || optimisticItem.song,
          })

          void songsAPI.wakeProcessingJob(result.jobId).catch(error => {
            console.warn('Lyrics cleanup is queued, but the worker wake request failed:', error)
          })
        }

        return result
      }

      // Convex reactive query will update the queue automatically via useConvexSync
      const queuePosition = Number.isFinite(result?.position) ? result.position + 1 : null
      toast.success(queuePosition ? `Song added to queue (#${queuePosition})!` : 'Song added to queue!')
      return result
    } catch (error) {
      console.error('Failed to add song to queue:', error)

      // Roll back optimistic update
      set(state => ({
        queue: state.queue.filter(item => item.id !== optimisticId)
      }))

      const message = String(error?.message || '')
      if (message.includes('Queue is full')) {
        toast.error('Queue is full right now')
      } else if (message.toLowerCase().includes('not ready yet')) {
        toast.error('Song is still processing. Try again soon.')
      } else if (message.toLowerCase().includes('room not found')) {
        toast.error('Room is unavailable. Refresh and try again.')
      } else {
        toast.error('Failed to add song to queue')
      }
      throw error
    }
  },

  /**
   * Remove song from queue via Convex mutation
   */
  removeFromQueue: async (roomCode, itemId) => {
    const { queue } = get()

    const itemToRemove = queue.find(item => item.id === itemId)
    if (!itemToRemove) {
      console.warn('Item not found in queue:', itemId)
      return
    }
    if (itemToRemove.status === 'playing') {
      toast.error('Cannot remove the currently playing song')
      return
    }

    // Optimistic update
    const optimisticQueue = queue.filter(item => item.id !== itemId)
    set({ queue: optimisticQueue })

    try {
      const useRoomStore = (await import('./roomStore')).default
      const currentRoomId = useRoomStore.getState().roomId

      await convex.mutation(api.queue.removeFromQueue, {
        itemId,
        roomId: currentRoomId,
      })
      toast.success('Song removed from queue!')
    } catch (error) {
      console.error('Failed to remove song:', error)

      // Restore on failure
      const { queue: currentQueue } = get()
      const restoredQueue = [...currentQueue]
      const insertIndex = Math.min(itemToRemove.position, restoredQueue.length)
      restoredQueue.splice(insertIndex, 0, itemToRemove)

      set({ queue: restoredQueue })
      toast.error('Failed to remove song from queue')
    }
  },

  /**
   * Reorder queue items via Convex mutation
   */
  reorderQueue: async (roomCode, items) => {
    const { queue } = get()
    let effectiveRoomId = null

    // Save snapshot for rollback
    const previousQueue = queue.map(item => ({ ...item }))

    // Build position lookup
    const positionMap = new Map(
      items.map(({ id, position }) => [id?.toString(), position])
    )

    const optimisticallyUpdatedQueue = queue
      .map(item => {
        const mappedPosition = positionMap.get(item.id?.toString())
        if (mappedPosition === undefined) return item
        return { ...item, position: mappedPosition }
      })
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

    set({ queue: optimisticallyUpdatedQueue })

    try {
      if (roomCode) {
        const room = await convexClient.query(api.rooms.getByCode, { code: roomCode })
        effectiveRoomId = room?._id ?? room?.id ?? null
      }

      if (!effectiveRoomId) {
        const useRoomStore = (await import('./roomStore')).default
        effectiveRoomId = useRoomStore.getState().roomId
      }

      if (!effectiveRoomId) {
        throw new Error('Room not connected.')
      }

      await convex.mutation(api.queue.reorderQueue, {
        roomId: effectiveRoomId,
        items: items.map(({ id, position }) => ({
          id,
          position
        }))
      })
    } catch (error) {
      // Rollback
      set({ queue: previousQueue })
      throw error
    }
  },

  /**
   * Update item status in queue
   */
  updateItemStatus: (itemId, status) => {
    set(state => ({
      queue: state.queue.map(item =>
        item.id === itemId ? { ...item, status } : item
      )
    }))
  },

  /**
   * Mark item at position as played
   */
  markPositionAsPlayed: (position) => {
    set(state => ({
      queue: state.queue.map(item =>
        item.position === position ? { ...item, status: 'played' } : item
      )
    }))
  },

  /**
   * Refresh queue (no-op - Convex reactive queries handle refresh automatically)
   */
  refreshQueue: async () => {},

  /**
   * Reset store state
   */
  reset: () => {
    set({ queue: [] })
  }
}))

// Selector hooks for granular subscriptions

export const useQueue = () => useQueueStore(state => state.queue)

export const useQueueHelpers = () => useQueueStore(
  state => ({
    getPreviousSong: state.getPreviousSong,
    getCurrentSong: state.getCurrentSong,
    getNextSong: state.getNextSong,
    getActiveQueue: state.getActiveQueue,
    getHistory: state.getHistory
  }),
  shallow
)

export default useQueueStore
